import { createContext, useContext, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useParams, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, ScrollText, UserPlus, Smile,
  ClipboardList, AlertTriangle, Users, UserPlus2, Mail, KeyRound, Lock, LifeBuoy, Search, Copy, Vote,
} from 'lucide-react';
import { api, avatarUrl, guildIconUrl } from '../api.js';

const GuildContext = createContext(null);
export const useGuild = () => useContext(GuildContext);

const NAV_SERVER = [
  { to: 'overview', label: 'Overview', Icon: LayoutDashboard },
];
const NAV_CONFIG = [
  { to: 'config/logs',      label: 'Logging',        Icon: ScrollText },
  { to: 'config/welcome',   label: 'Welcome',        Icon: UserPlus },
  { to: 'config/reactions', label: 'Reaction Roles', Icon: Smile },
  { to: 'config/mirrors',   label: 'Mirror & Archive', Icon: Copy },
];
const NAV_MOD = [
  { to: 'audit',    label: 'Audit search', Icon: Search },
  { to: 'modlog',   label: 'Mod log',  Icon: ClipboardList },
  { to: 'warnings', label: 'Warnings', Icon: AlertTriangle },
  { to: 'members',  label: 'Members',  Icon: Users },
  { to: 'invites',  label: 'Invites',  Icon: UserPlus2 },
  { to: 'bot-dms',  label: 'Bot DMs',  Icon: Mail },
  { to: 'channel-mods', label: 'Channel Mods', Icon: KeyRound },
  { to: 'restrictions', label: 'Restrictions', Icon: Lock },
  { to: 'tickets',      label: 'Support Tickets', Icon: LifeBuoy },
  { to: 'polls',        label: 'Polls',          Icon: Vote },
];

export default function GuildLayout({ me, setMe }) {
  const { guildId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    setState({ loading: true });
    api.guild(guildId)
      .then(data => setState({ loading: false, ...data }))
      .catch(err => setState({ loading: false, error: err.message }));
  }, [guildId]);

  async function handleLogout() {
    await api.logout();
    setMe({ loading: false, user: null });
  }

  if (state.loading) {
    return (
      <div className="app">
        <div className="topbar">
          <div className="brand">
            <img className="brand-icon" src="/soupos-icon.png" alt="" /> SoupOS
          </div>
        </div>
        <div className="content"><div className="empty">Loading server…</div></div>
      </div>
    );
  }
  if (state.error) return <div className="empty">Error: {state.error}</div>;

  const isOwnerView = me.isOwner && !me.manageable?.some(g => g.id === guildId);

  return (
    <GuildContext.Provider value={{ guildId, guild: state.guild, channels: state.channels }}>
      <div className="app">
        <div className="topbar">
          <div className="brand">
            <span style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '.5rem' }} onClick={() => navigate('/')}>
              <img className="brand-icon" src="/soupos-icon.png" alt="" /> SoupOS
            </span>
            <span className="dim">/</span>
            {guildIconUrl(state.guild)
              ? <img className="avatar" src={guildIconUrl(state.guild)} alt="" />
              : <div className="avatar" />}
            <span>{state.guild.name}</span>
          </div>
          <div className="row" style={{ gap: '.75rem', flex: 0 }}>
            {me.isOwner && <Link to="/owner" className="btn ghost">Owner panel</Link>}
            <img className="avatar" src={avatarUrl(me.user)} alt="" />
            <span>{me.user.global_name || me.user.username}</span>
            <button className="ghost" onClick={handleLogout}>Log out</button>
          </div>
        </div>

        <div className="layout">
          <nav className="sidebar">
            <div className="nav-label">Server</div>
            <div className="nav-section">
              {NAV_SERVER.map(item => (
                <NavLink key={item.to} className={({ isActive }) => 'item' + (isActive ? ' active' : '')} to={`/g/${guildId}/${item.to}`}>
                  <span className="item-icon"><item.Icon size={18} strokeWidth={2} /></span>
                  <span className="item-label">{item.label}</span>
                </NavLink>
              ))}
            </div>
            <div className="nav-label">Config</div>
            <div className="nav-section">
              {NAV_CONFIG.map(item => (
                <NavLink key={item.to} className={({ isActive }) => 'item' + (isActive ? ' active' : '')} to={`/g/${guildId}/${item.to}`}>
                  <span className="item-icon"><item.Icon size={18} strokeWidth={2} /></span>
                  <span className="item-label">{item.label}</span>
                </NavLink>
              ))}
            </div>
            <div className="nav-label">Moderation</div>
            <div className="nav-section">
              {NAV_MOD.map(item => (
                <NavLink key={item.to} className={({ isActive }) => 'item' + (isActive ? ' active' : '')} to={`/g/${guildId}/${item.to}`}>
                  <span className="item-icon"><item.Icon size={18} strokeWidth={2} /></span>
                  <span className="item-label">{item.label}</span>
                </NavLink>
              ))}
            </div>
            <div className="nav-spacer" />
            <div className="sidebar-footer">
              <div><Link to="/">← All servers</Link></div>
              <div className="ver" style={{ marginTop: 4 }}>SoupOS · dum4hub</div>
            </div>
          </nav>

          <div className="content">
            <ServerHero guild={state.guild} isOwnerView={isOwnerView} />
            <Outlet />
          </div>
        </div>
      </div>
    </GuildContext.Provider>
  );
}

function ServerHero({ guild, isOwnerView }) {
  return (
    <div className="server-hero">
      <div
        className="icon-wrap"
        style={guildIconUrl(guild) ? { backgroundImage: `url(${guildIconUrl(guild)})` } : undefined}
      >
        {!guildIconUrl(guild) && guild.name.slice(0, 1).toUpperCase()}
      </div>
      <div style={{ position: 'relative', zIndex: 1, flex: 1 }}>
        <h2 className="name">
          {guild.name}
          {isOwnerView && <span className="badge accent" style={{ marginLeft: 10, verticalAlign: 'middle' }}>owner view</span>}
        </h2>
        <div className="meta">
          <span><span className="dot" />{guild.online_count ?? '—'} online</span>
          <span>{guild.member_count ?? '—'} members</span>
          {guild.description && <span style={{ opacity: .8 }}>· {guild.description}</span>}
        </div>
      </div>
    </div>
  );
}
